<template>
	<div class="price-wrapper" :style="wrapperMargin">
		<div v-if="quotePrice && !product.isTire">
			<span class="price-quote">
				Request a quote for pricing.
			</span>
		</div>
		<div v-else-if="priceVaries">
			<span class="price-quote">
				Pricing varies, call or visit local store for details.
				<span
					@click="$emit('show-change-store')"
					class="link-to-modal"
					tabindex="0"
					aria-label="Find a store"
					@keydown.enter="$emit('show-change-store')"
				>
					Find a Store
				</span>
			</span>
		</div>
		<div v-else-if="comingSoon"></div>
		<div v-else>
			<template v-if="price.pricePromoText">
				<ui-flag v-if="isClearance" color="blue" class="clearance-flag">
					{{ promoText }}
				</ui-flag>
				<div v-else class="promo-text">{{ promoText }}</div>
			</template>
			<div v-if="!price.hideWasPrice && !currentPriceOnly && price.originalPrice" class="original-price">
				<span class="original-price-prefix">
					{{ price.originalPricePrefixText ? price.originalPricePrefixText : 'Was ' }}
				</span>
				<span :class="price.strikeThroughWasPrice ? 'original-price-span' : 'original-price-span-no-strikethru'">
					{{ price.originalPrice }}
				</span>
				<span v-if="price.originalPriceSuffixText" class="original-price-suffix">
					{{ price.originalPriceSuffixText }}
				</span>
			</div>
			<div
				v-if="price.priceSavingsText && !currentPriceOnly"
				class="price-saved"
			>
				{{ price.priceSavingsText }}
			</div>

			<div v-if="price.priceAltText" class="price-alt">
				<!-- Render price alt text and prevent tool tip from wrapping alone -->
				<span
					v-for="(word, i) in wordsInPriceAltText"
					:key="i"
					:class="i == wordsInPriceAltText.length - 1 && 'price-alt__last-word'"
				>
					{{ word }}
					<price-tool-tip
						v-if="i == wordsInPriceAltText.length - 1 && !comingSoon"
						class="price-alt-tip"
						:is-tire="product.isTire"
					/>
				</span>
			</div>
			<template v-else>
				<div class="current-price">
					<span
						v-if="price.pricePrefixText"
						:class="['price-prefix', {'sale-span': product.isOnSale}]"
					>
						{{ price.pricePrefixText }}
					</span>
					<span :class="'price'">
						<div v-if="isGroupPrice">
							<div aria-hidden="true">
								<span class="dollar-sign">$</span>{{ splitPrice(price.lowPrice).d }}<span class="price-cents">{{ splitPrice(price.lowPrice).c }}</span>
								<span class="dash">&#8212;</span>
								<span class="dollar-sign">$</span>{{ splitPrice(price.highPrice).d }}<span class="price-cents">{{ splitPrice(price.highPrice).c }}</span>
							</div>

							<span class="visually-hidden">from ${{ price.lowPrice.replace('$', '') }} to ${{ price.highPrice.replace('$', '') }}</span>
						</div>
						<div v-else>
							<div aria-hidden="true">
								<span class="dollar-sign">$</span>{{ splitPrice(price.price).d }}<span class="price-cents">{{ splitPrice(price.price).c }}</span>
							</div>
							<span class="visually-hidden">${{ price.price.replace('$', '') }}</span>
						</div>
					</span>
					<span v-if="price.priceCommentText" class="price-comment">
						{{ price.priceCommentText }}
					</span>
				</div>
				<div v-if="price.priceSuffixText" class="price-suffix">
					{{ price.priceSuffixText }}
				</div>
			</template>
		</div>
		<div v-if="price.isClearance" class="pdp-tire-limited-availability">
			Limited availability in select stores only. Please call your store for details.
		</div>
	</div>
</template>

<script>
import PriceToolTip from '../../components/price/PriceToolTip.vue';
import UiFlag from '../../components/UiFlag.vue';

export default {
	name: "ProductPrice",
	props: {
		price: {
			type: Object,
			required: true
		},
		currentPriceOnly: Boolean,
	},
	components: {
		PriceToolTip,
		UiFlag,
	},
	computed: {
		product() {
			return this.$store.state.product;
		},
		currentZip() {
			return this.$store.state.currentZip;
		},
		comingSoon() {
			return this.product.isComingSoon;
		},
		priceVaries() {
			return this.product.showInStoreOnlyMessage && this.price.hidePrice && !this.price.isBelowMAP;
		},
		quotePrice() {
			return this.product.showGetQuote && this.price.hidePrice;
		},
		isAuto() {
			return this.product.isTire || this.product.isAutomotiveBattery;
		},
		isMobile() {
			return this.$mq === 'pdp-mobile';
		},
		promoText() {
			if (this.price.isClearance) return 'Clearance';
			return this.price.pricePromoText;
		},
		wrapperMargin() {
			return this.currentPriceOnly ? '' : 'margin-bottom: 15px;';
		},
		isClearance() {
			return this.price.isClearance || this.price.discontinued;
		},
		isGroupPrice() {
			return this.price.lowPrice && this.price.lowPrice !== 0
				&& this.price.highPrice && this.price.highPrice !== 0;
		},
		wordsInPriceAltText() {
			return this.price.priceAltText.split(' ');
		}
	},
	methods: {
		splitPrice(price) {
			price = price.toString();
			if (!price || price.indexOf(".") === -1) return;
			const d = price.split(".")[0].replace('$', ''),
				c = price.split(".")[1];
			return { d, c };
		},
	},
};
</script>

<style lang="less" scoped>
@import '../../../less/_mixins.less';
.original-price {
	color: #666;
	font-size: 14px;
	margin-bottom: 5px;
	font-weight: 500;
	.original-price-span {
		text-decoration: line-through;
		margin: 0 2px;
	}
	.original-price-span-no-strikethru {
		margin: 0 2px;
	}
}
.clearance-flag {
	margin-bottom: 0.25rem;
}
.price {
	div {
		font-size: 38px;
		font-weight: 500;
		display: flex;
		flex-direction: row;
		align-items: flex-start;
		* {
			padding-right: 2px;
		}
		.dash {
			align-self: center;
			margin: 0 2px;
		}
		span {
			// vertical-align: super;
			padding: 4px 3px 0 1px;
			font-size: 50%;
		}
		.dollar-sign {
			align-self: flex-start;
			padding: 4px 3px 0 0px;
			font-size: 21px;
		}
		.price-cents {
			align-self: flex-start;
			font-size: 22px;
		}
	}
}

.price-alt {
	display: inline-block;
	line-height: 1.2;


	&__last-word {
		display: inline-flex;
	}
}

/deep/ .blain-tooltip {
	.mobile-styles({
		max-width: 310px;

		.info-window-text {
			margin-right: -10px;
		}
	});
}

.current-price {
	display: flex;
	max-width: 510px;
}

.price-saved {
	color: #B12F31;
	font-weight: 500;
	font-size: 16px;
	border-bottom: 1px solid #CCCCCC;
	max-width: 300px;
	padding: 2px 0 5px 0;
	margin-bottom: 4px;
}
.price-alt {
	font-size: 22px;
	max-width: 380px;
}
.price-quote {
	color: #666666;
	font-size: 16px;
	text-align: right;
}
.link-to-modal {
	color: #014A77;
	text-decoration: underline;
	cursor: pointer;
}
.sale-span {
	color: #B12F31;
	text-transform: uppercase;
	font-size: 18px;
	font-weight: bold;
	text-decoration: none!important;
}
.price-comment {
	display: flex;
	align-self: flex-end;
	margin-bottom: 5px;
	margin-left: 5px;
}

.price-alt-tip {
	display: inline-block;
	margin-left: 0.25em;
	white-space: normal;
}
.price-prefix {
	font-weight: bold;
	font-size: 18px;
	margin-right: 9px;
	margin-top: 5px;
}
.price-suffix {
	color: #777777;
	font-size: 12px;
}
.promo-text {
	color: #666;
	font-weight: 500;
	margin-bottom: 5px;
}

.pdp-tire-limited-availability {
	color: shade(@CL, 40%);
	font-size: 12px;
	max-width: 240px;
	line-height: 1.2;
}

.mobile-styles({
	.price-wrapper {
		margin-top: 15px;
	}
	.clearance {
		font-size: 13px;
	}
	.sale-span {
		font-size: 14px;
	}
	.original-price {
		padding: 5px 0 0 0;
		font-size: 14px;
		.price-saved {
			padding-top: 1px;
			margin: 0;
		}
	}
	.price-alt {
		font-size: 20px;
	}
});

@media screen and (max-width: @tablet) {
	.price div{
		font-size: 30px;
		.price-cents, .dollar-sign {
			font-size: 18px;
			padding: 2px 0 0 0;
			font-size: 20px;
			padding: 2px 0 0 0;
		}
	}
}

@media screen and (max-width: @phablet) {
	.price-wrapper {
		margin-top: 15px;
	}
	.clearance {
		font-size: 13px;
	}
	.sale-span {
		font-size: 14px;
	}
	.original-price {
		padding: 5px 0 0 0;
		font-size: 14px;
		.price-saved {
			padding-top: 1px;
			margin: 0;
		}
	}
	.price-alt {
		font-size: 20px;
	}
}

@media screen and (max-width: @mobile-small) {
	.price div{
		font-size: 30px;
		.price-cents {
			font-size: 14px;
			padding: 2px 0 0 0;
		}
		.dollar-sign {
			font-size: 20px;
			padding: 2px 0 0 0;
		}
	}
	.original-price {
		font-size: 10px;
	}
	.sale-span {
		font-size: 12px;
	}
	.clearance-flag {
		font-size: 10px;
	}
	.price-saved {
		font-size: 14px;
	}
	.price-suffix {
		margin-top: 2px;
		font-size: 10px;
	}
	.price-prefix {
		margin-right: 3px;
	}
}
</style>
